import React, { useState, useEffect, useCallback } from "react";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

// feat_289 — Editor de feriados por empresa.
//   - Nacionales AR: toggle "trabajamos ese día" (national-override).
//   - Custom: feriados propios (recurrentes o fecha única).
// Consume /settings/holidays (isAuth + getTenantId; companyId del token).

const fmtDate = ymd => {
  if (!ymd || typeof ymd !== "string") return ymd;
  const [y, m, d] = ymd.split("-");
  if (!d) return ymd;
  return `${d}/${m}/${y}`;
};

const HolidaysEditor = () => {
  const year = new Date().getFullYear();
  const [nationals, setNationals] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", recurring: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/settings/holidays?year=${year}`);
      setNationals(data.nationals || []);
      setCustoms(data.customs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleNationalWorks = async (date, works) => {
    try {
      await api.post("/settings/holidays/national-override", { date, works });
      setNationals(prev =>
        prev.map(h => (h.date === date ? { ...h, works } : h))
      );
    } catch (err) {
      toastError(err);
    }
  };

  const addCustom = async () => {
    if (!form.date) return;
    try {
      await api.post("/settings/holidays/custom", form);
      toast.success(i18n.t("holidays.saveOk"));
      setAddOpen(false);
      setForm({ date: "", name: "", recurring: false });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const removeCustom = async idx => {
    if (!window.confirm(i18n.t("holidays.confirmDelete"))) return;
    try {
      await api.delete(`/settings/holidays/custom/${idx}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div>
      <Typography variant="subtitle2" gutterBottom>
        {i18n.t("holidays.nationals")} — {year}
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        {i18n.t("holidays.nationalsHelp")}
      </Typography>
      <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 16 }}>
        {nationals.map(h => (
          <div
            key={h.date}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "2px 0"
            }}
          >
            <span style={{ fontSize: 13 }}>
              {fmtDate(h.date)} — {h.name}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Typography variant="caption" color="textSecondary">
                {h.works ? i18n.t("holidays.works") : i18n.t("holidays.closed")}
              </Typography>
              <Switch
                size="small"
                color="primary"
                checked={!!h.works}
                onChange={e => toggleNationalWorks(h.date, e.target.checked)}
              />
            </span>
          </div>
        ))}
      </div>

      <Typography variant="subtitle2" gutterBottom>
        {i18n.t("holidays.customs")}
      </Typography>
      {customs.length === 0 && (
        <Typography variant="caption" color="textSecondary" display="block">
          {i18n.t("holidays.noCustoms")}
        </Typography>
      )}
      {customs.map((h, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "2px 0"
          }}
        >
          <span style={{ fontSize: 13 }}>
            {fmtDate(h.date)} — {h.name}
            {h.recurring && ` (${i18n.t("holidays.recurring")})`}
          </span>
          <IconButton size="small" onClick={() => removeCustom(idx)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setAddOpen(true)}
        style={{ textTransform: "none", marginTop: 8 }}
        disabled={loading}
      >
        {i18n.t("holidays.addCustom")}
      </Button>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("holidays.addCustom")}</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            label={i18n.t("holidays.dateLabel")}
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
          />
          <TextField
            label={i18n.t("holidays.nameLabel")}
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="dense"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.recurring}
                onChange={e => setForm({ ...form, recurring: e.target.checked })}
                color="primary"
              />
            }
            label={i18n.t("holidays.recurringLabel")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{i18n.t("holidays.cancel")}</Button>
          <Button onClick={addCustom} color="primary" disabled={!form.date}>
            {i18n.t("holidays.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HolidaysEditor;
